/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cn, lodash } from '@ba-ui-toolkit/utils';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import Button from '@ba-ui-toolkit/button';
import Tooltip from '@ba-ui-toolkit/tooltip';
import FlexLayout from '@ba-ui-toolkit/flexlayout';
import menuOverflowIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_16.js';
import { AutoSizer, MultiGrid, CellMeasurer, CellMeasurerCache } from 'react-virtualized';

import ArrowKeyStepper from './ArrowKeyStepper';
import stringResource from './nls/StringResource';
import styles from './Grid.scss';

const { isEqual, uniqueId } = lodash;
const gridClassName = styles.gridClassName.replace(/"/g, '');
const defaultGridAttributes = {
	ROW_OPTIONS_COLUMN_WIDTH: 40,
	ROW_HEIGHT: 40,
	COLUMN_WIDTH: 170,
	SCROLL_TO_COLUMN: -1,
	SCROLL_TO_ROW: -1,
	MAX_COLUMN_WIDTH: 400,
	OVERSCAN_COLUMN_COUNT: 10,
	OVERSCAN_ROW_COUNT: 20,
};

function isAutoMeasurePropsValid(
	{ autoMeasureColumnWidth, autoMeasureRowHeight, columnWidth, rowHeight },
	propName,
	componentName
) {
	if (propName === 'autoMeasureColumnWidth' && autoMeasureColumnWidth) {
		if (!rowHeight) {
            return console.warn( // eslint-disable-line
				"If 'autoMeasureColumnWidth' is passed, it must be accompanied with the measurement prop 'rowHeight', or default will be used."
			);
		}

		if (columnWidth) {
			return new Error(
				`Invalid prop '${propName}' passed to '${componentName}'. If 'autoMeasureColumnWidth' is passed, then you cannot pass 'columnWidth'.`
			);
		}
	}

	if (propName === 'autoMeasureRowHeight' && autoMeasureRowHeight) {
		if (!columnWidth) {
            return console.warn( // eslint-disable-line
				"If 'autoMeasureRowHeight' is passed, it must be accompanied with the measurement prop 'columnWidth', or default will be used."
			);
		}

		if (rowHeight) {
			return new Error(
				`Invalid prop '${propName}' passed to '${componentName}'. If 'autoMeasureRowHeight' is passed, then you cannot pass 'rowHeight'.`
			);
		}
	}

	return null;
}

export default class Grid extends Component {
	static propTypes = {
		/** Let the grid auto measure a cell's width only, cannot be used with columnWidth. */
		autoMeasureColumnWidth: PropTypes.bool && isAutoMeasurePropsValid,
		/** Let the grid auto measure a cell's height only, cannot be used with rowHeight. */
		autoMeasureRowHeight: PropTypes.bool && isAutoMeasurePropsValid,
		/** Class attributes applied to the grid container */
		className: PropTypes.string,
		/** Callback function that returns the width of a column given its index, defaults to 170. */
		columnWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
		/** Callback function that returns cell element for given row and column index for the data part of grid */
		dataCellRenderer: PropTypes.func.isRequired,
		/** String for bidi support */
		dir: PropTypes.oneOf(['ltr', 'rtl']),
		/** Number of fixed columns in grid, defaults to 0 */
		fixedColumns: PropTypes.number,
		/** ID of svg icon to be used in row options menu cell (temporary hack by design) */
		fixedCornerCellMenuIcon: PropTypes.object,
		/** Callback function that returns cell element for given row and column index for fixed column part of grid */
		fixedColumnCellRenderer: PropTypes.func,
		/** Callback function that returns cell element for given row and column index for fixed corner part of grid */
		fixedCornerCellRenderer: PropTypes.func,
		/** Callback function that returns cell element for given row and column index for fixed row part of grid */
		fixedRowCellRenderer: PropTypes.func,
		/** Number of fixed row headers in grid, defaults to 0 */
		fixedRows: PropTypes.number,
		/** Indices of the focused cell */
		focusedCell: PropTypes.object,
		/** Callback that is called when the bottom of the grid is reached. */
		getMoreData: PropTypes.func,
		/** Maximum column width value */
		maxColumnWidth: PropTypes.number,
		/** Number of columns in grid */
		numColumns: PropTypes.number.isRequired,
		/** Number of rows in grid */
		numRows: PropTypes.number.isRequired,
		/** Callback function that is called when a grid has updated. It passes in a range object with the visible rows and columns */
		onAfterGridUpdate: PropTypes.func,
		/** Callback when cell focus is changed */
		onCellFocus: PropTypes.func,
		/** Callback when the corner grid menu is clicked */
		onFixedCornerCellMenu: PropTypes.func,
		/** Callback when fixed cell header is clicked in grid. useRowOptionsColumn is required */
		onFixedRowCellMenu: PropTypes.func,
		/** Count of overscan column */
		overscanColumnCount: PropTypes.number,
		/** Count of overscan row */
		overscanRowCount: PropTypes.number,
		/** Callback function that returns the height of a row given its index, defaults to 40. */
		rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
		/** Text that will be shown in the corner cell menu tooltip */
		rowOptionsColumnText: PropTypes.string,
		/** Array of number of columns to highlight as selected */
		selectedColumns: PropTypes.array,
		/** Styles to be applied to the grid container */
		style: PropTypes.object,
		/** Enable row options column */
		useRowOptionsColumn: PropTypes.bool,
	};

	static defaultProps = {
		selectedColumns: [],
		fixedRows: 0,
		fixedColumns: 0,
		getMoreData: () => null,
		onAfterGridUpdate: () => null,
		autoMeasureColumnWidth: false,
		autoMeasureRowHeight: false,
		dataCellRenderer: () => null,
		fixedColumnCellRenderer: () => null,
		fixedCornerCellRenderer: () => null,
		fixedRowCellRenderer: () => null,
		onFixedRowCellMenu: () => null,
		rowOptionsColumnText: stringResource.get('changeGridSettings'),
		dir: 'ltr',
		className: '',
		style: {},
		maxColumnWidth: defaultGridAttributes.MAX_COLUMN_WIDTH,
	};

	state = {
		scrollToAlignment: 'auto',
		isFocused: false,
		focusedCellContent: null,
	};

	_multiGridRef = null;
	_multiGridKey = uniqueId();

	constructor(props) {
		super(props);
		this._initializeCellMeasureCache();
	}

	componentWillMount() {
		const { focusedCell } = this.props;
		const defaultFocusedCell = {
			columnIndex: defaultGridAttributes.SCROLL_TO_COLUMN,
			rowIndex: defaultGridAttributes.SCROLL_TO_ROW,
		};

		this.setState({ focusedCell: focusedCell || defaultFocusedCell });
	}

	componentWillReceiveProps(nextProps) {
		// Reset scroll indices when there are new/empty selected columns
		if (!isEqual(nextProps.selectedColumns, this.props.selectedColumns)) {
			this._resetScrollToIndices();
		}

		const didAutoMeasureChange =
			this._shouldAutoMeasureColumnWidth(nextProps) !==
				this._shouldAutoMeasureColumnWidth() ||
			this._shouldAutoMeasureRowHeight(nextProps) !== this._shouldAutoMeasureRowHeight();

		// The grid doesn't properly update the cache, when we switch auto measurement props.
		// Therefore, we manually create new caches per auto measurement props change.
		if (didAutoMeasureChange) {
			this._initializeCellMeasureCache();
		}

		// The multigrid doesn't properly update these values change (deferredMeasurementCache, fixedColumnCount, fixedRowCount)
		// Therefore, we'll need to force a remount of the component.
		if (
			didAutoMeasureChange ||
			this._getFixedRows(nextProps) !== this._getFixedRows() ||
			this._getColumnCount(nextProps) !== this._getColumnCount()
		) {
			this._multiGridKey = uniqueId();
		}

		const { focusedCell } = nextProps;

		if (focusedCell) {
			this.setState({ focusedCell });
		}
	}

	setRowScrollIndex = ({ index }) => {
		this.setState({
			focusedCell: {
				columnIndex: 0,
				rowIndex: index + this._getFixedRows(),
			},
		});
	};

	setColumnScrollIndex = ({ index }) => {
		this.setState({
			focusedCell: {
				columnIndex: index + this._getFixedColumns(),
				rowIndex: 0,
			},
		});
	};

	/**
	 * From react virtualized docs:
	 * Invalidate Grids and recompute visible cells.
	 * Recomputes row heights and column widths after the specified index (both default to 0).
	 * Refer to Grid->invalidateCellSizeAfterRender()
	 * @param {number} rowIndex
	 * @param {number} columnIndex
	 */
	invalidateCellSizeAfterRender = ({ rowIndex = 0, columnIndex = 0 } = {}) => {
		this._multiGridRef &&
			this._multiGridRef.invalidateCellSizeAfterRender &&
			this._multiGridRef.invalidateCellSizeAfterRender({ rowIndex, columnIndex });
	};

	/**
	 * From react virtualized docs:
	 * This function should be called if dynamic column or row sizes have changed but nothing else has.
	 * Since Grid only receives columnCount and rowCount it has no way of detecting when the underlying data changes.
	 * Refer to Grid->recomputeGridSize()
	 * @param {number} rowIndex
	 * @param {number} columnIndex
	 */
	recomputeGridSize = ({ rowIndex = 0, columnIndex = 0 } = {}) => {
		this._multiGridRef &&
			this._multiGridRef.recomputeGridSize &&
			this._multiGridRef.recomputeGridSize({ rowIndex, columnIndex });
	};

	forceUpdateGrids() {
		this._multiGridRef &&
			this._multiGridRef.forceUpdateGrids &&
			this._multiGridRef.forceUpdateGrids();
	}

	_initializeCellMeasureCache = () => {
		if (this._shouldAutoMeasureColumnWidth()) {
			this._cellMeasurerCache = new CellMeasurerCache({
				minHeight: defaultGridAttributes.ROW_HEIGHT,
				minWidth: defaultGridAttributes.COLUMN_WIDTH,
				fixedHeight: true,
			});
		} else if (this._shouldAutoMeasureRowHeight()) {
			this._cellMeasurerCache = new CellMeasurerCache({
				minHeight: defaultGridAttributes.ROW_HEIGHT,
				minWidth: defaultGridAttributes.COLUMN_WIDTH,
				fixedWidth: true,
			});
		}
	};

	_resetScrollToIndices = () => {
		this.setState({
			focusedCell: {
				columnIndex: defaultGridAttributes.SCROLL_TO_COLUMN,
				rowIndex: defaultGridAttributes.SCROLL_TO_ROW,
			},
		});
	};

	_shouldAutoMeasureColumnWidth = (props = this.props) =>
		!!(props.autoMeasureColumnWidth && !props.columnWidth);

	_shouldAutoMeasureRowHeight = (props = this.props) =>
		!!(props.autoMeasureRowHeight && !props.rowHeight);

	_onSectionRendered = ({ rowStopIndex, columnStopIndex, rowStartIndex, columnStartIndex }) => {
		const { getMoreData, onAfterGridUpdate } = this.props;

		onAfterGridUpdate &&
			onAfterGridUpdate({
				rowEnd: rowStopIndex,
				colEnd: columnStopIndex,
				rowStart: rowStartIndex,
				colStart: columnStartIndex,
			});

		const rowOffset = this._getRowCount() - this._getFixedRows() - 1;

		if (rowStopIndex === rowOffset) {
			getMoreData && getMoreData();
		}
	};

	_getColumnCount = (props = this.props) => props.numColumns + this._getFixedColumns(props);

	_getRowCount = (props = this.props) => props.numRows + this._getFixedRows(props);

	_getFixedColumns = (props = this.props) => {
		const fixedColumns = (props.fixedColumns || 0) + (props.useRowOptionsColumn ? 1 : 0);

		return fixedColumns > 0 ? fixedColumns : 0;
	};

	_getFixedRows = (props = this.props) => props.fixedRows;

	_getRowOverscan = (props = this.props) => {
		const overscan = props.overscanRowCount;

		return overscan === 0 || overscan > 0 ? overscan : defaultGridAttributes.OVERSCAN_ROW_COUNT;
	};

	_getColumnOverscan = (props = this.props) => {
		const overscan = props.overscanColumnCount;

		return overscan === 0 || overscan > 0
			? overscan
			: defaultGridAttributes.OVERSCAN_COLUMN_COUNT;
	};

	/**
	 * Only want the header to be scrollable when no data exists
	 */
	_isFixedRowScrollable = () => this.props.numRows === 0 && this.props.fixedRows > 0;

	_bidiStyles = () => {
		if (this.props.dir === 'rtl') {
			// HACK: react-virtualized (RV) doesn't support RTL, we perform a rotational transform
			// to get around this issue. Note that RV explicitly sets direction to ltr as a
			// band aid solution since rtl breaks the grid.
			// Refer to https://github.com/bvaughn/react-virtualized/issues/454
			return {
				transform: 'rotateY(180deg)',
				WebkitFontSmoothing: 'antialiased',
				justifyContent: 'flex-end',
			};
		}

		return {
			justifyContent: 'flex-start',
		};
	};

	/**
	 * Contrain the column at a max width.
	 * @param {number} width - the width we want to constrain
	 */
	_constrainColumnWidth = width => Math.min(width, this.props.maxColumnWidth);

	_cellRenderer = ({ columnIndex, key, rowIndex, style = {}, parent }) => {
		const { focusedCell } = this.state;
		const focusedClassname = {
			'cell-is-focused':
				focusedCell.columnIndex === columnIndex && focusedCell.rowIndex === rowIndex,
		};
		const { useRowOptionsColumn, onFixedCornerCellMenu } = this.props;
		const fixedColumns = this._getFixedColumns();
		const fixedRows = this._getFixedRows();

		// HACK: When using CellMeasurer with MultiGrid there is a bug. There are two cases to consider:
		// 1. fixedHeight: true => width will auto measure. Produces correct behaviour.
		// 2. fixedWidth: true => height will auto measure. Incorrect behaviour.
		// It seems that when CellMeasurer._getCellMeasurements runs, node.style.width is auto which
		// means height is being measured before the before the width is applied. To remedy this,
		// we force set the dimension styles when it's auto.
		// Refer to https://github.com/bvaughn/react-virtualized/issues/1354
		if (this._shouldAutoMeasureRowHeight()) {
			if (style.width === 'auto') {
				const { columnWidth } = this._getColumnWidthProps();

				style.width = columnWidth({ index: columnIndex });
			} else {
				style.width = this._constrainColumnWidth(style.width);
			}

			style.whiteSpace = 'normal';
		}

		if (this._shouldAutoMeasureColumnWidth()) {
			style.whiteSpace = 'nowrap';
		}

		const cellBaseProps = {
			cellOnClick: () =>
				this._selectCell({
					scrollToColumn: columnIndex,
					scrollToRow: rowIndex,
				}),
			className: focusedClassname,
			key,
			style: {
				...style,
				...this._bidiStyles(),
			},
		};
		let cell = null;

		if (useRowOptionsColumn && columnIndex === 0) {
			if (rowIndex === 0 && onFixedCornerCellMenu) {
				cell = this._fixedCornerCellRenderer({
					...cellBaseProps,
					rowIndex,
				});
			} else {
				cell = this._fixedColumnCellRenderer({
					...cellBaseProps,
				});
			}
		} else if (columnIndex < fixedColumns && rowIndex < fixedRows) {
			cell = this._fixedCornerCellRenderer({
				...cellBaseProps,
				columnIndex,
				rowIndex,
			});
		} else if (columnIndex < fixedColumns) {
			cell = this._fixedColumnCellRenderer({
				...cellBaseProps,
				columnIndex,
				rowIndex: rowIndex - fixedRows,
			});
		} else if (rowIndex < fixedRows) {
			cell = this._fixedRowCellRenderer({
				...cellBaseProps,
				columnIndex: columnIndex - fixedColumns,
				rowIndex,
			});
		} else {
			cell = this._cellRendererMain({
				...cellBaseProps,
				columnIndex: columnIndex - fixedColumns,
				rowIndex: rowIndex - fixedRows,
			});
		}

		if (this._shouldAutoMeasureColumnWidth() || this._shouldAutoMeasureRowHeight()) {
			return (
				<CellMeasurer
					key={key}
					parent={parent}
					cache={this._cellMeasurerCache}
					columnIndex={columnIndex}
					rowIndex={rowIndex}
				>
					{cell}
				</CellMeasurer>
			);
		}

		return cell;
	};

	_hasClickableHeaderMenu = row => {
		const fixedRows = this._getFixedRows();

		return row === 0 && fixedRows > 0 && this.props.onFixedRowCellMenu;
	};

	_onCornerMenuClicked = e => {
		e.preventDefault();
		e.stopPropagation();
		let { clientX, clientY } = e;
		if (clientX === 0 && clientY === 0) {
			const { x, y } = e.currentTarget.getBoundingClientRect();
			clientX = x;
			clientY = y;
		}
		this.props.onFixedCornerCellMenu &&
			this.props.onFixedCornerCellMenu(clientX, clientY, 0, 0);
	};

	_fixedCornerCellRenderer = ({ className, columnIndex, key, rowIndex, style, cellOnClick }) => {
		const {
			fixedCornerCellMenuIcon,
			rowOptionsColumnText,
			dir,
			useRowOptionsColumn,
		} = this.props;
		const borderClassName = dir === 'rtl' ? { 'rtl-border': true } : { 'ltr-border': true };

		if (useRowOptionsColumn) {
			return (
				<div
					className={cn(
						`${gridClassName}__top-left-grid-cell`,
						className,
						borderClassName
					)}
					key={key}
					style={style}
					onClick={cellOnClick}
				>
					<FlexLayout justifyContent="center" height="100%" direction="column">
						<Tooltip title={rowOptionsColumnText}>
							<Button
								fullWidth
								onClick={this._onCornerMenuClicked}
								onContextMenu={this._onCornerMenuClicked}
								tabIndex={0}
								variant="inline"
								data-tid="ba-common-grid__menu-button"
								icon={
									<SVGIcon
										pad
										iconId={
											fixedCornerCellMenuIcon
												? fixedCornerCellMenuIcon.id
												: menuOverflowIcon.id
										}
										size="small"
									/>
								}
							/>
						</Tooltip>
					</FlexLayout>
				</div>
			);
		}

		const { fixedCornerCellRenderer } = this.props;
		const gridCellProps = {
			className: cn('top-left-grid-cell', className),
			style,
			key,
			onClick: cellOnClick,
			dir,
		};

		return fixedCornerCellRenderer(rowIndex, columnIndex, gridCellProps);
	};

	_fixedColumnCellRenderer = ({ className, columnIndex, key, rowIndex, style, cellOnClick }) => {
		const borderClassName =
			this.props.dir === 'rtl' ? { 'rtl-border': true } : { 'ltr-border': true };

		if (this.props.useRowOptionsColumn) {
			return (
				<div
					className={cn(
						`${gridClassName}__bottom-left-grid-cell`,
						className,
						borderClassName
					)}
					key={key}
					style={style}
					onClick={cellOnClick}
				>
					<div style={{ width: defaultGridAttributes.ROW_OPTIONS_COLUMN_WIDTH }} />
				</div>
			);
		}

		const { fixedColumnCellRenderer, dir } = this.props;
		const gridCellProps = {
			className: cn('bottom-left-grid-cell', className),
			style,
			key,
			onClick: cellOnClick,
			dir,
		};

		return fixedColumnCellRenderer(rowIndex, columnIndex, gridCellProps);
	};

	_cellRendererMain = ({ className, columnIndex, key, rowIndex, style, cellOnClick }) => {
		const { selectedColumns, dataCellRenderer, dir } = this.props;
		const gridCellProps = {
			className: cn('bottom-right-grid-cell', className, {
				'cell-is-selected': selectedColumns.includes(columnIndex),
			}),
			style,
			key,
			onClick: cellOnClick,
			dir,
		};

		return dataCellRenderer(rowIndex, columnIndex, gridCellProps);
	};

	_fixedRowCellOnMenu = (rowIndex, columnIndex, cellOnClick) => e => {
		if (this._hasClickableHeaderMenu(rowIndex)) {
			e.preventDefault();
			this.props.onFixedRowCellMenu(e.clientX, e.clientY, rowIndex, columnIndex);
		}
		cellOnClick();
	};

	_fixedRowCellRenderer = ({ className, columnIndex, key, rowIndex, style, cellOnClick }) => {
		const { selectedColumns, fixedRowCellRenderer, dir } = this.props;
		const gridCellProps = {
			className: cn(className, {
				'top-right-grid-cell header-cell': rowIndex === 0,
				'bottom-right-grid-cell': rowIndex > 0,
				'cell-is-selected': selectedColumns.includes(columnIndex),
			}),
			style,
			key,
			onClick: this._fixedRowCellOnMenu(rowIndex, columnIndex, cellOnClick),
			onContextMenu: this._fixedRowCellOnMenu(rowIndex, columnIndex, cellOnClick),
			dir,
		};

		return fixedRowCellRenderer(rowIndex, columnIndex, gridCellProps);
	};

	_selectCell = ({ scrollToColumn, scrollToRow }) => {
		const { onCellFocus, focusedCell } = this.props;

		if (!focusedCell) {
			this.setState({
				focusedCell: {
					columnIndex: scrollToColumn,
					rowIndex: scrollToRow,
				},
			});
		}

		onCellFocus &&
			onCellFocus({
				columnIndex: scrollToColumn,
				rowIndex: scrollToRow,
			});
	};

	_getFocusedCellContent = () => {
		const {
			focusedCell: { rowIndex, columnIndex },
		} = this.state;

		// Set focused contents when within grid boundaries
		return rowIndex >= 0 && columnIndex >= 0
			? this._cellRenderer({
					columnIndex,
					rowIndex,
			  })
			: null;
	};

	_makeConstrained = fn => ref => this._constrainColumnWidth(fn(ref));

	/**
	 * Get the proper column width props
	 * TODO: If function is used, specify `estimatedRowSize` for more consistent scrolling behavior.
	 */
	_getColumnWidthProps = () => {
		const { columnWidth, useRowOptionsColumn } = this.props;
		let columnWidthProp = null;

		if (this._shouldAutoMeasureColumnWidth()) {
			columnWidthProp = ref => {
				if (useRowOptionsColumn && ref.index === 0) {
					return defaultGridAttributes.ROW_OPTIONS_COLUMN_WIDTH;
				}

				return this._cellMeasurerCache.columnWidth(ref);
			};
		} else if (typeof columnWidth === 'function') {
			columnWidthProp = ({ index }) => {
				if (useRowOptionsColumn && index === 0) {
					return defaultGridAttributes.ROW_OPTIONS_COLUMN_WIDTH;
				}

				return columnWidth(index);
			};
		} else {
			columnWidthProp = ({ index }) => {
				if (useRowOptionsColumn && index === 0) {
					return defaultGridAttributes.ROW_OPTIONS_COLUMN_WIDTH;
				}

				return defaultGridAttributes.COLUMN_WIDTH;
			};
		}

		return {
			columnWidth: this._shouldAutoMeasureColumnWidth()
				? columnWidthProp
				: this._makeConstrained(columnWidthProp),
		};
	};

	/**
	 * Get the proper row height props
	 * TODO: If function is used, specify `estimatedColumnSize` for more consistent scrolling behavior.
	 */
	_getRowHeightProps = () => {
		const { rowHeight } = this.props;

		if (this._shouldAutoMeasureRowHeight()) {
			return {
				rowHeight: this._cellMeasurerCache.rowHeight,
			};
		} else if (typeof rowHeight === 'function') {
			return {
				rowHeight: ({ index }) => rowHeight(index),
			};
		}

		return {
			rowHeight: () => defaultGridAttributes.ROW_HEIGHT,
		};
	};

	_getMeasurementCacheProps = () => {
		if (this._shouldAutoMeasureColumnWidth() || this._shouldAutoMeasureRowHeight()) {
			return {
				deferredMeasurementCache: this._cellMeasurerCache,
			};
		}

		return {};
	};

	render() {
		const adjustedColumnCount = this._getColumnCount();
		const adjustedRowCount = this._getRowCount();
		const adjustedFixedColumns = this._getFixedColumns();
		const adjustedFixedRows = this._getFixedRows();
		const isFixedRowScrollable = this._isFixedRowScrollable();
		const { focusedCell, scrollToAlignment } = this.state;
		const { onFixedCornerCellMenu, onFixedRowCellMenu, style, className } = this.props;

		return (
			<ArrowKeyStepper
				className={`${gridClassName} full-height ${className}`}
				columnCount={adjustedColumnCount}
				onScrollToChange={this._selectCell}
				mode="cells"
				rowCount={adjustedRowCount}
				scrollToColumn={focusedCell.columnIndex}
				scrollToRow={focusedCell.rowIndex}
				fixedColumns={adjustedFixedColumns}
				fixedRows={adjustedFixedRows}
				onFixedCornerCellMenu={onFixedCornerCellMenu}
				onFixedRowCellMenu={onFixedRowCellMenu}
				style={style}
			>
				{({ scrollToColumn, scrollToRow, registerChild }) => (
					<div
						className={`${gridClassName}__arrow-key-stepper full-height`}
						tabIndex={0}
						onBlur={() => this._resetScrollToIndices()}
					>
						{/*
                            1. To announce content changes aria-live region must be "visible"
                            2. Need to re-render the aria-live region with every cell focus
                               since regions are read only when live region appear in the DOM
                               or when inner text is changed. Necessary for duplicate content.
                        */}
						<div
							key={`${scrollToColumn}_${scrollToRow}`}
							aria-live="polite"
							className={`${gridClassName} visually-hidden`}
						>
							{this._getFocusedCellContent()}
						</div>
						<AutoSizer>
							{({ width, height }) => (
								<MultiGrid
									{...this._getMeasurementCacheProps()}
									{...this._getColumnWidthProps()}
									{...this._getRowHeightProps()}
									scrollToAlignment={scrollToAlignment}
									scrollToColumn={scrollToColumn}
									scrollToRow={scrollToRow}
									cellRenderer={this._cellRenderer}
									columnCount={adjustedColumnCount}
									fixedRowCount={adjustedFixedRows}
									fixedColumnCount={adjustedFixedColumns}
									rowCount={adjustedRowCount}
									height={height - 1}
									width={width - 1}
									overscanColumnCount={this._getColumnOverscan()}
									overscanRowCount={this._getRowOverscan()}
									onSectionRendered={this._onSectionRendered}
									enableFixedRowScroll={isFixedRowScrollable}
									hideTopRightGridScrollbar={isFixedRowScrollable}
									ref={node => {
										registerChild(node);
										this._multiGridRef = node;
									}}
									classNameBottomLeftGrid={`${gridClassName}__bottom-left-grid`}
									classNameBottomRightGrid={`${gridClassName}__bottom-right-grid`}
									classNameTopLeftGrid={`${gridClassName}__top-left-grid`}
									classNameTopRightGrid={`${gridClassName}__top-right-grid`}
									style={this._bidiStyles()}
									tabIndex={-1}
									key={this._multiGridKey}
								/>
							)}
						</AutoSizer>
					</div>
				)}
			</ArrowKeyStepper>
		);
	}
}
